import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CabinetHeader } from "../components/CabinetHeader";
import { Button } from "../components/Button";
import { getCabinetMe } from "../api/cabinet";
import { listPosts } from "../api/posts";
import { getAutoImports } from "../api/auto_imports";
import type { ModerationStatus, Post, PostType, AutoImportItem } from "../types/gordost";

/**
 * Главная кабинета «Гордость 94» — «летопись достижений ребёнка».
 *
 * Редизайн (вариант В): hero ребёнка, инфо-панель с контекстом приёма,
 * единая хронологическая лента (школа + публикации родителя) по месяцам,
 * карточки-обложки с превью фото и цветом по типу, сезонные акценты.
 */

// ── Метаданные типов достижений ──────────────────────────────────────
// Swiss-унификация: единый нейтральный тинт, тип различается эмодзи + подписью.
const POST_TYPE_TINT = "bg-[#f4f4f5] text-[var(--color-ink)]";
const POST_TYPE_META: Record<
  PostType,
  { icon: string; label: string; tint: string }
> = {
  diploma: { icon: "🏆", label: "Диплом", tint: POST_TYPE_TINT },
  project: { icon: "🎯", label: "Проект", tint: POST_TYPE_TINT },
  initiative: { icon: "✨", label: "Инициатива", tint: POST_TYPE_TINT },
  gratitude: { icon: "💝", label: "Благодарность", tint: POST_TYPE_TINT },
  sport: { icon: "⚽", label: "Спорт", tint: POST_TYPE_TINT },
};

const SOURCE_LABEL: Record<AutoImportItem["source"], string> = {
  competition: "Олимпиада / конкурс",
  youth_movement: "Движение",
  award: "Награда",
};

const MONTHS_RU = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

export function CabinetPage() {
  const { data: me, isLoading, error } = useQuery({
    queryKey: ["cabinet-me"],
    queryFn: getCabinetMe,
  });

  const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
  const childId = selectedChildId ?? me?.children[0]?.id ?? null;
  const yearId = me?.current_year?.id ?? null;

  const { data: autoData, isLoading: autoLoading } = useQuery({
    queryKey: ["auto-imports", childId, yearId],
    queryFn: () =>
      childId
        ? getAutoImports(childId, yearId ?? undefined)
        : Promise.resolve({ items: [], total: 0 }),
    enabled: !!childId,
  });

  const { data: postsData, isLoading: postsLoading } = useQuery({
    queryKey: ["posts", childId, yearId],
    queryFn: () =>
      childId
        ? listPosts({ student_id: childId, year_id: yearId ?? undefined })
        : Promise.resolve({ items: [], total: 0 }),
    enabled: !!childId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)] flex items-center justify-center">
        <p className="text-[var(--color-muted)]">Загрузка…</p>
      </div>
    );
  }
  if (error || !me) {
    return (
      <div className="min-h-screen bg-[var(--color-paper)] flex items-center justify-center">
        <p className="text-[var(--color-cobalt)]">Ошибка загрузки кабинета</p>
      </div>
    );
  }

  const child = me.children.find((c) => c.id === childId) ?? null;
  const posts = postsData?.items ?? [];
  const autoItems = autoData?.items ?? [];

  // Счётчики статусов.
  const stats = posts.reduce(
    (acc, p) => {
      acc[p.moderation_status] = (acc[p.moderation_status] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );
  const approvedCount = stats.approved ?? 0;
  const needsRevisionCount = stats.needs_revision ?? 0;

  const loading = autoLoading || postsLoading;
  const totalItems = posts.length + autoItems.length;
  const phase = seasonalPhase();

  return (
    <div className="min-h-screen bg-[var(--color-paper)]">
      <CabinetHeader displayName={me.parent.display_name} />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-6 sm:py-10 pb-24">
        {/* ── Переключатель детей (если >1) ─────────────────────────── */}
        {me.children.length > 1 && (
          <div className="-mx-5 px-5 mb-5 flex gap-2 overflow-x-auto no-scrollbar">
            {me.children.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedChildId(c.id)}
                className={`snap-start shrink-0 px-4 py-3 rounded-full text-sm font-[600] border transition-colors ${
                  childId === c.id
                    ? "bg-[var(--color-ink)] text-white border-[var(--color-ink)]"
                    : "bg-white border-[var(--color-line)] text-[var(--color-ink)] hover:border-[var(--color-cobalt)]"
                }`}
              >
                {firstTwoNames(c.full_name)}
                {c.class_name && <span className="opacity-60"> · {c.class_name}</span>}
              </button>
            ))}
          </div>
        )}

        {me.children.length === 0 ? (
          <NoChildren />
        ) : (
          <>
            {/* ── Hero ребёнка (Swiss, как лендинг) ──────────────────── */}
            <section className="mb-7 border-b border-[var(--color-ink)] pb-7">
              <div className="flex items-center gap-2.5 mb-4 text-[11px] font-bold tracking-[0.16em] uppercase text-[var(--color-cobalt)]">
                <span className="w-9 h-[3px] bg-[var(--color-cobalt)] shrink-0" />
                <span>
                  Том I · {me.current_year?.name ?? ""} · Летопись достижений
                </span>
              </div>
              <h1 className="font-[200] leading-[0.9] tracking-[-0.04em] text-4xl sm:text-6xl">
                {child?.full_name ?? "Мой кабинет"}
              </h1>
              {child?.class_name && (
                <div className="text-[var(--color-muted)] mt-2 text-sm">
                  {child.class_name} класс
                </div>
              )}
              <div className="flex items-end justify-between gap-4 mt-6">
                <div className="font-[200] leading-[0.8] tracking-[-0.04em] text-[var(--color-cobalt)] text-6xl sm:text-8xl tabular-nums">
                  {approvedCount}
                </div>
                <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--color-muted)] text-right font-bold max-w-[130px] pb-1">
                  {pluralizeAch(approvedCount)} в&nbsp;портфолио
                </div>
              </div>
            </section>

            {/* ── Инфо-панель: что это / приём / кто видит ──────────── */}
            <div className="mb-6 grid sm:grid-cols-3 gap-px bg-[var(--color-ink)] border border-[var(--color-ink)] text-sm">
              <div className="bg-[var(--color-paper)] p-4">
                <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-[var(--color-muted)] mb-1">
                  Что это
                </div>
                <div className="text-[var(--color-ink)] leading-snug">
                  Летопись достижений ребёнка для личного приёма у директора.
                </div>
              </div>
              <div className="bg-[var(--color-paper)] p-4">
                <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-[var(--color-muted)] mb-1">
                  Ближайший приём
                </div>
                <div className="font-[700] text-[var(--color-gold)]">
                  {me.reception_label ?? "Май"}
                </div>
              </div>
              <div className="bg-[var(--color-paper)] p-4">
                <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-[var(--color-muted)] mb-1">
                  Кто видит
                </div>
                <div className="text-[var(--color-ink)] leading-snug">
                  Вы, классный руководитель и директор. Никаких лент и лайков.
                </div>
              </div>
            </div>

            {/* ── Сводка статусов ───────────────────────────────────── */}
            {posts.length > 0 && (
              <div className="mb-6 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[var(--color-ink)] border border-[var(--color-ink)]">
                <StatCell n={approvedCount} label="опубликовано" tone="cobalt" />
                <StatCell n={stats.pending ?? 0} label="на проверке" tone="amber" />
                <StatCell n={needsRevisionCount} label="нужны правки" tone="orange" />
                <StatCell n={autoData?.total ?? 0} label="от школы" tone="muted" />
              </div>
            )}

            {/* ── Призыв «нужны правки» ─────────────────────────────── */}
            {needsRevisionCount > 0 && (
              <div className="mb-6 p-4 bg-[#fff5f4] border-l-4 border-[var(--color-red)]">
                <p className="text-sm">
                  <span className="font-bold">
                    {needsRevisionCount === 1
                      ? "1 публикация ждёт"
                      : `${needsRevisionCount} публикаций ждут`}{" "}
                    ваших правок.
                  </span>{" "}
                  Учитель оставил комментарий — откройте публикацию ниже, внесите
                  изменения и отправьте снова.
                </p>
              </div>
            )}

            {/* ── Сезонная подсказка + CTA ──────────────────────────── */}
            <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-[var(--color-muted)]">{phase}</p>
              <div className="flex items-center gap-2">
                {childId && approvedCount > 0 && (
                  <Link
                    to={`/cabinet/year-summary?student_id=${childId}`}
                    className="inline-flex items-center gap-1.5 text-sm font-[600] text-[var(--color-cobalt)] border border-[var(--color-cobalt)] px-4 py-2.5 hover:bg-[#eef1ff] transition-colors"
                  >
                    ✦ Сводка года
                  </Link>
                )}
                {childId && (
                  <Link to={`/cabinet/post/new?student_id=${childId}`}>
                    <Button variant="primary">+ Опубликовать достижение</Button>
                  </Link>
                )}
              </div>
            </div>

            {/* ── Контент: онбординг / скелетон / летопись / пусто ──── */}
            {loading ? (
              <TimelineSkeleton />
            ) : totalItems === 0 ? (
              <Onboarding childId={childId} childName={child?.full_name} />
            ) : (
              <>
                <Highlights posts={posts} />
                <div className="flex items-baseline gap-3 mb-4">
                  <h2
                    className="text-2xl leading-tight"
                    style={{ fontFamily: "var(--font-serif)" }}
                  >
                    Летопись по месяцам
                  </h2>
                </div>
                <Timeline posts={posts} autoItems={autoItems} />
              </>
            )}
          </>
        )}
      </main>

      {/* Sticky-FAB на мобильном */}
      {childId && me.children.length > 0 && (
        <Link
          to={`/cabinet/post/new?student_id=${childId}`}
          className="sm:hidden fixed bottom-5 right-5 z-20 w-14 h-14 rounded-full bg-[var(--color-cobalt)] text-white grid place-items-center text-3xl shadow-lg active:scale-95 transition-transform"
          aria-label="Опубликовать достижение"
        >
          +
        </Link>
      )}
    </div>
  );
}

// ── Единая лента-летопись по месяцам ──────────────────────────────────

type TimelineEntry =
  | { kind: "post"; date: string; post: Post }
  | { kind: "auto"; date: string; auto: AutoImportItem };

function Timeline({ posts, autoItems }: { posts: Post[]; autoItems: AutoImportItem[] }) {
  const entries: TimelineEntry[] = [
    ...posts.map(
      (p): TimelineEntry => ({
        kind: "post",
        date: p.event_date ?? p.created_at,
        post: p,
      }),
    ),
    ...autoItems.map(
      (a): TimelineEntry => ({ kind: "auto", date: a.event_date, auto: a }),
    ),
  ];
  // Сортировка по дате убыванием (свежее сверху).
  entries.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  // Группировка по месяцу.
  const groups: { key: string; label: string; items: TimelineEntry[] }[] = [];
  for (const e of entries) {
    const d = new Date(e.date);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const label = `${MONTHS_RU[d.getMonth()]} ${d.getFullYear()}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(e);
    else groups.push({ key, label, items: [e] });
  }

  return (
    <div className="space-y-8">
      {groups.map((g) => (
        <section key={g.key}>
          <div className="flex items-baseline justify-between mb-4 border-t border-[var(--color-line)] pt-3">
            <h2 className="text-xl leading-tight" style={{ fontFamily: "var(--font-serif)" }}>{g.label}</h2>
            <span className="text-[11px] text-[var(--color-muted)] tabular-nums">
              {g.items.length} {pluralizeRecords(g.items.length)}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.items.map((e) =>
              e.kind === "post" ? (
                <AchievementCard key={`p${e.post.id}`} post={e.post} />
              ) : (
                <AutoCard key={e.auto.id} item={e.auto} />
              ),
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

// ── Bento «Главное за год» (D1: лучшие одобренные достижения) ─────────

function Highlights({ posts }: { posts: Post[] }) {
  const top = posts
    .filter((p) => p.moderation_status === "approved")
    .sort((a, b) => {
      const da = a.event_date ?? a.created_at;
      const db = b.event_date ?? b.created_at;
      return da < db ? 1 : da > db ? -1 : 0;
    })
    .slice(0, 5);
  if (top.length === 0) return null;
  return (
    <section className="mb-10">
      <div className="flex items-baseline gap-3 mb-4">
        <h2
          className="text-2xl leading-tight"
          style={{ fontFamily: "var(--font-serif)" }}
        >
          Главное за год
        </h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 auto-rows-[150px] sm:auto-rows-[170px] gap-4">
        {top.map((p, i) => (
          <HighlightCard key={p.id} post={p} big={i === 0} />
        ))}
      </div>
    </section>
  );
}

function HighlightCard({ post, big }: { post: Post; big: boolean }) {
  const cover = post.files.find(
    (f) => f.mime_type.startsWith("image/") && f.download_url,
  );
  const t = POST_TYPE_META[post.post_type];
  return (
    <Link
      to={`/cabinet/post/${post.id}`}
      className={`card-soft group relative overflow-hidden flex flex-col ${
        big ? "col-span-2 row-span-2" : ""
      }`}
    >
      <div
        className={`relative flex-1 overflow-hidden grid place-items-center ${
          cover ? "bg-[var(--color-ink)]" : t.tint
        }`}
      >
        {cover ? (
          <img
            src={cover.download_url!}
            alt={post.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
          />
        ) : (
          <span className={big ? "text-6xl" : "text-4xl"}>{t.icon}</span>
        )}
        <span
          className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 text-[10px] font-bold bg-white text-[var(--color-cobalt)] px-2 py-1 shadow-sm"
          title={confirmedLabel(post.confirmed_level)}
        >
          ✓{big && <span className="hidden sm:inline">&nbsp;Подтверждено</span>}
        </span>
      </div>
      <div className="p-3 sm:p-4 border-t border-[var(--color-line)] bg-white">
        <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.1em] uppercase text-[var(--color-muted)] mb-1.5">
          <span>{t.icon}</span>
          <span>{t.label}</span>
        </div>
        <div
          className={`font-[600] leading-snug group-hover:text-[var(--color-cobalt)] transition-colors ${
            big ? "text-base line-clamp-3" : "text-sm line-clamp-2"
          }`}
        >
          {post.title}
        </div>
        {big && post.event_date && (
          <div className="mt-2 text-xs text-[var(--color-muted)] tabular-nums">
            {new Date(post.event_date).toLocaleDateString("ru-RU")}
          </div>
        )}
      </div>
    </Link>
  );
}

// ── Карточка публикации родителя (обложка) ───────────────────────────

function AchievementCard({ post }: { post: Post }) {
  const cover = post.files.find(
    (f) => f.mime_type.startsWith("image/") && f.download_url,
  );
  const t = POST_TYPE_META[post.post_type];

  return (
    <Link to={`/cabinet/post/${post.id}`} className="card-soft group block overflow-hidden">
      <div
        className={`relative aspect-[16/10] overflow-hidden ${cover ? "bg-[var(--color-ink)]" : t.tint}`}
      >
        {cover ? (
          <img
            src={cover.download_url!}
            alt={post.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl">
            {t.icon}
          </div>
        )}
        <div className="absolute top-3 right-3">
          <StatusChip status={post.moderation_status} />
        </div>
        {!cover && post.external_url && (
          <span className="absolute bottom-3 left-3 text-[11px] font-bold tracking-wide uppercase bg-black/70 text-white px-2 py-1 rounded-full">
            ▶ видео
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className={`w-7 h-7 rounded-full grid place-items-center text-sm ${t.tint}`}>
            {t.icon}
          </span>
          <span className="text-[11px] font-bold tracking-[0.12em] uppercase text-[var(--color-muted)]">
            {t.label}
          </span>
        </div>
        <h3 className="font-[600] leading-snug line-clamp-2 mb-2 group-hover:text-[var(--color-cobalt)] transition-colors">
          {post.title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-[var(--color-muted)] tabular-nums">
          {post.event_date && (
            <span>{new Date(post.event_date).toLocaleDateString("ru-RU")}</span>
          )}
          {post.files.length > 0 && (
            <span>· {post.files.length} {pluralizeFiles(post.files.length)}</span>
          )}
        </div>
        {post.moderation_status === "approved" && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide text-[var(--color-cobalt)] bg-[#eef1ff] px-2.5 py-1">
            <span aria-hidden>✓</span>
            <span>{confirmedLabel(post.confirmed_level)}</span>
          </div>
        )}
        {post.moderation_status === "needs_revision" && post.moderation_comment && (
          <div className="mt-3 text-xs bg-[#fff5f4] text-[var(--color-red)] px-3 py-2 line-clamp-2">
            ✏️ Учитель просит: «{post.moderation_comment}»
          </div>
        )}
      </div>
    </Link>
  );
}

// ── Карточка авто-достижения (из школы, read-only) ───────────────────

function AutoCard({ item }: { item: AutoImportItem }) {
  return (
    <div className="card-soft p-4 flex gap-3 items-start">
      <span className="w-10 h-10 rounded-full bg-[var(--color-cobalt)]/10 text-[var(--color-cobalt)] grid place-items-center text-xl shrink-0">
        {item.icon}
      </span>
      <div className="min-w-0">
        <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-[var(--color-muted)] mb-1">
          {SOURCE_LABEL[item.source]} · от школы
        </div>
        <div className="font-[600] leading-snug">{item.title}</div>
        {item.subtitle && (
          <div className="text-sm text-[var(--color-muted)] mt-0.5 line-clamp-1">
            {item.subtitle}
          </div>
        )}
        {item.event_date && (
          <div className="text-xs text-[var(--color-muted)] mt-1 tabular-nums">
            {new Date(item.event_date).toLocaleDateString("ru-RU")}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Онбординг (0 достижений) ─────────────────────────────────────────

function Onboarding({
  childId,
  childName,
}: {
  childId: number | null;
  childName?: string | null;
}) {
  const examples = [
    { icon: "🏆", text: "Грамота с конкурса или олимпиады" },
    { icon: "🤝", text: "Добрый поступок — помог однокласснику, участвовал в акции" },
    { icon: "🎯", text: "Проект, поделка, исследование" },
    { icon: "⚽", text: "Победа или участие в спортивных соревнованиях" },
  ];
  const firstName = childName?.split(" ")[1] ?? childName?.split(" ")[0] ?? "ребёнка";
  return (
    <div className="card-soft border-dashed p-6 sm:p-10 text-center">
      <div className="text-5xl mb-4">📖</div>
      <h3 className="font-[600] text-lg sm:text-xl mb-2">Летопись пока пуста</h3>
      <p className="text-sm text-[var(--color-muted)] max-w-md mx-auto mb-6">
        Опубликуйте первое достижение {firstName}. Классный руководитель проверит
        его за 1–3 дня — и оно станет частью портфолио, которое увидит директор.
      </p>
      <div className="max-w-md mx-auto mb-7 space-y-2 text-left">
        <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-[var(--color-muted)] mb-2 text-center">
          Что можно опубликовать
        </div>
        {examples.map((e) => (
          <div
            key={e.text}
            className="flex items-center gap-3 bg-[var(--color-paper)] border border-[var(--color-line)] rounded-xl px-3 py-2.5 text-sm"
          >
            <span className="text-lg">{e.icon}</span>
            <span>{e.text}</span>
          </div>
        ))}
      </div>
      {childId && (
        <Link to={`/cabinet/post/new?student_id=${childId}`}>
          <Button variant="primary">Добавить первое достижение</Button>
        </Link>
      )}
    </div>
  );
}

function NoChildren() {
  return (
    <div className="card-soft border-dashed p-6 sm:p-10 text-center mt-4">
      <div className="text-5xl mb-4">👋</div>
      <h3 className="font-[600] text-lg sm:text-xl mb-2">Добро пожаловать в «Гордость 94»</h3>
      <p className="text-sm text-[var(--color-muted)] max-w-md mx-auto mb-6">
        К аккаунту пока не привязан ни один ребёнок. Добавьте ребёнка по коду —
        его выдаёт классный руководитель (QR-код или короткий код).
      </p>
      <Link to="/cabinet/profile">
        <Button variant="primary">Добавить ребёнка по коду</Button>
      </Link>
    </div>
  );
}

// ── Скелетон ─────────────────────────────────────────────────────────

function TimelineSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="card-soft overflow-hidden">
          <div className="aspect-[16/10] bg-[var(--color-line)] animate-pulse" />
          <div className="p-4 space-y-2">
            <div className="h-3 w-20 bg-[var(--color-line)] rounded animate-pulse" />
            <div className="h-4 w-3/4 bg-[var(--color-line)] rounded animate-pulse" />
            <div className="h-3 w-1/3 bg-[var(--color-line)] rounded animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Мелкие компоненты ────────────────────────────────────────────────

/** Текст метки признания по уровню подтверждения (A1.1).
 *  null (старые/без уровня) → обобщённая «Подтверждено школой». */
function confirmedLabel(level: string | null): string {
  if (level === "admin") return "Подтверждено администрацией";
  if (level === "classroom") return "Подтверждено классным руководителем";
  return "Подтверждено школой";
}

function StatusChip({ status }: { status: ModerationStatus }) {
  const map: Record<ModerationStatus, [string, string, string]> = {
    approved: ["bg-white text-[var(--color-cobalt)]", "bg-[var(--color-cobalt)]", "Опубликовано"],
    pending: ["bg-white text-[var(--color-muted)]", "bg-[var(--color-muted)]", "На проверке"],
    needs_revision: ["bg-white text-[var(--color-red)]", "bg-[var(--color-red)]", "Нужны правки"],
    rejected: ["bg-white text-[var(--color-red)]", "bg-[var(--color-red)]", "Отклонено"],
  };
  const [cls, dot, label] = map[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold shadow-sm ${cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function StatCell({
  n,
  label,
  tone,
}: {
  n: number;
  label: string;
  tone: "cobalt" | "amber" | "orange" | "muted";
}) {
  const color = {
    cobalt: "text-[var(--color-cobalt)]",
    amber: "text-[var(--color-ink)]",
    orange: "text-[var(--color-red)]",
    muted: "text-[var(--color-ink)]",
  }[tone];
  return (
    <div className="bg-[var(--color-paper)] p-4 sm:p-5">
      <div className={`font-[800] tabular-nums leading-none text-3xl sm:text-4xl ${color}`}>
        {n}
      </div>
      <div className="text-[11px] tracking-[0.12em] uppercase text-[var(--color-muted)] mt-2">
        {label}
      </div>
    </div>
  );
}

// ── Утилиты ──────────────────────────────────────────────────────────

function firstTwoNames(fullName: string): string {
  return fullName.split(" ").slice(0, 2).join(" ");
}

function seasonalPhase(): string {
  const m = new Date().getMonth(); // 0..11
  if (m >= 8 && m <= 9) return "📖 Летопись открыта — самое время начать том этого года.";
  if (m >= 3 && m <= 4)
    return "🎓 Скоро приём — проверьте, что лучшие достижения одобрены классным руководителем.";
  if (m >= 5 && m <= 7) return "☀️ Летопись года сохранена. Новые записи — с сентября.";
  return "✍️ Продолжайте летопись — добавляйте достижения ребёнка в течение года.";
}

function pluralizeAch(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "достижение";
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "достижения";
  return "достижений";
}

function pluralizeFiles(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "файл";
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "файла";
  return "файлов";
}

function pluralizeRecords(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "запись";
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "записи";
  return "записей";
}
